import { useState, useEffect } from 'react';
import { api } from '../lib/api.jsx';
import { useTableSort } from '../lib/useTableSort.jsx';

const inputS =
  'w-full bg-card border border-rule-2 rounded-lg px-3 py-2 text-fg-2 text-[13px] outline-none box-border';

// Chevron des <select> (appearance-none supprime celui du navigateur).
const chevronBg =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")";

export function GroupsPage() {
  const [groups, setGroups] = useState([]);
  const [clients, setClients] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
  });
  const [editingId, setEditingId] = useState(null);
  const [editTab, setEditTab] = useState('general');
  const [editForm, setEditForm] = useState({});
  const [memberSearch, setMemberSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [addClientLevel, setAddClientLevel] = useState(2);
  const { sortRows, headerProps, arrow } = useTableSort('name', 'asc');
  const load = async () => {
    const [g, c, u] = await Promise.all([
      api.get('/platform/groups'),
      api.get('/clients'),
      api.get('/users'),
    ]);
    setGroups(g || []);
    setClients(c || []);
    setAllUsers(u || []);
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  // editing is derived from groups — always fresh after reload
  const editing = editingId ? groups.find((x) => x.id === editingId) || null : null;
  const reload = async () => {
    const [g, c, u] = await Promise.all([
      api.get('/platform/groups'),
      api.get('/clients'),
      api.get('/users'),
    ]);
    setGroups(g || []);
    setClients(c || []);
    setAllUsers(u || []);
  };
  const handleCreate = async () => {
    if (!createForm.name) return alert('Nom requis');
    await api.post('/platform/groups', createForm);
    setShowCreate(false);
    setCreateForm({
      name: '',
      description: '',
      canRemoteConnect: false,
      canRunCommands: false,
      canManageAlerts: true,
      canViewInventory: true,
    });
    load();
  };
  const handleDelete = async (id) => {
    if (!confirm('Supprimer ce groupe et toutes ses associations?')) return;
    await api.del('/platform/groups/' + id);
    setEditingId(null);
    load();
  };
  const openEdit = (g) => {
    setEditingId(g.id);
    reload(); // refresh clients/users list so newly-created ones appear
    setEditForm({
      name: g.name,
      description: g.description || '',
    });
    setEditTab('general');
    setMemberSearch('');
    setClientSearch('');
  };
  const saveEdit = async () => {
    await api.put('/platform/groups/' + editingId, editForm);
    reload();
  };
  const addMember = async (userId) => {
    await api.post('/platform/groups/' + editingId + '/members', {
      userId,
    });
    await reload();
  };
  const removeMember = async (userId) => {
    await api.del('/platform/groups/' + editingId + '/members/' + userId);
    await reload();
  };
  const addClient = async (clientId) => {
    await api.post('/platform/groups/' + editingId + '/clients', {
      clientId,
      accessLevel: addClientLevel,
    });
    setAddClientLevel(2);
    await reload();
  };
  const removeClient = async (clientId) => {
    await api.del('/platform/groups/' + editingId + '/clients/' + clientId);
    await reload();
  };
  // Le POST /clients est un upsert côté serveur : le réémettre sur un client déjà
  // accordé change son niveau et invalide le cache de portée de tous les membres.
  const changeClientLevel = async (clientId, accessLevel) => {
    await api.post('/platform/groups/' + editingId + '/clients', {
      clientId,
      accessLevel,
    });
    await reload();
  };
  const inputS =
    'w-full bg-card border border-rule-2 rounded-lg px-3 py-2 text-fg-2 text-[13px] outline-none box-border';
  const levelLabels = ['', 'Lecture', 'Opérateur', 'Contrôle total'];
  // Escalade de privilège : neutre → warn → crit. Tokens sémantiques plutôt que des
  // hex, pour que les pastilles restent lisibles en thème clair comme en sombre.
  const levelClasses = [
    '',
    'text-muted bg-muted/10',
    'text-warn bg-warn/10',
    'text-crit bg-crit/10',
  ];

  // Members tab data
  const availableUsers = editing
    ? allUsers
        .filter((u) => !editing.members.some((m) => m.userId === u.id))
        .filter(
          (u) =>
            !memberSearch ||
            u.email.toLowerCase().includes(memberSearch.toLowerCase()) ||
            (u.displayName || '').toLowerCase().includes(memberSearch.toLowerCase())
        )
    : [];
  // Clients tab data
  const availableClients = editing
    ? clients
        .filter((c) => !editing.clientAccess.some((ca) => ca.clientId === c.id))
        .filter((c) => !clientSearch || c.name.toLowerCase().includes(clientSearch.toLowerCase()))
    : [];
  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <h1
          className="text-[22px] font-bold text-fg"
          style={{
            margin: 0,
          }}
        >
          {"Groupes d'utilisateurs (" + groups.length + ')'}
        </h1>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="bg-blue-500 border-none rounded-lg py-2 px-[18px] text-white cursor-pointer text-xs font-semibold"
        >
          + Nouveau groupe
        </button>
      </div>
      {showCreate && (
        <div className="bg-card border border-rule rounded-2xl p-5 mb-5">
          <div className="text-sm font-bold text-blue-400 mb-1.5">Nouveau groupe</div>
          <div className="text-[11px] text-muted-2 mb-3.5">
            Les groupes déterminent quels clients un utilisateur peut voir. Les permissions d'actions sont
            gérées par les rôles.
          </div>
          <div
            className="grid gap-3 mb-3.5"
            style={{
              gridTemplateColumns: '1fr 1fr',
            }}
          >
            <input
              placeholder="Nom du groupe"
              value={createForm.name}
              onChange={(e) =>
                setCreateForm({
                  ...createForm,
                  name: e.target.value,
                })
              }
              className={inputS}
            />
            <input
              placeholder="Description (optionnel)"
              value={createForm.description}
              onChange={(e) =>
                setCreateForm({
                  ...createForm,
                  description: e.target.value,
                })
              }
              className={inputS}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCreate}
              className="bg-ok border-none rounded-lg py-2 px-5 text-white cursor-pointer text-xs font-semibold"
            >
              Créer
            </button>
            <button
              onClick={() => setShowCreate(false)}
              className="border border-rule-3 rounded-lg py-2 px-4 text-muted cursor-pointer text-xs"
              style={{
                background: 'none',
              }}
            >
              Annuler
            </button>
          </div>
        </div>
      )}
      {loading ? (
        <div className="text-muted-2">Chargement...</div>
      ) : groups.length === 0 ? (
        <div className="text-muted-2 p-10 text-center">Aucun groupe. Créez-en un pour gérer les accès.</div>
      ) : (
        <div className="bg-card border border-rule rounded-2xl overflow-hidden">
          <table
            className="w-full text-[12.5px]"
            style={{
              borderCollapse: 'collapse',
            }}
          >
            <thead>
              <tr className="border-b border-rule">
                {[
                  { key: 'name', label: 'Nom' },
                  { key: 'description', label: 'Description' },
                  { key: 'membersCount', label: 'Membres' },
                  { key: 'clientsCount', label: 'Clients' },
                  { key: null, label: 'Actions' },
                ].map((c) => (
                  <th
                    key={c.label}
                    {...headerProps(c.key)}
                    className="py-2.5 px-3.5 text-left text-muted-2 font-medium text-[11px] uppercase whitespace-nowrap"
                  >
                    {c.label}
                    {arrow(c.key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortRows(groups, (g, key) => {
                if (key === 'membersCount') return g.members?.length ?? 0;
                if (key === 'clientsCount') return g.clientAccess?.length ?? 0;
                return g[key];
              }).map((g) => {
                return (
                  <tr
                    key={g.id}
                    className="border-b border-rule cursor-pointer"
                    style={{
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(59,130,246,0.04)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <td className="py-3 px-3.5" onClick={() => openEdit(g)}>
                      <div className="font-semibold text-fg text-[13px]">{g.name}</div>
                    </td>
                    <td
                      className="py-3 px-3.5 text-muted-2 text-xs overflow-hidden truncate whitespace-nowrap"
                      style={{
                        maxWidth: 300,
                      }}
                      onClick={() => openEdit(g)}
                    >
                      {g.description || '—'}
                    </td>
                    <td className="py-3 px-3.5" onClick={() => openEdit(g)}>
                      <span
                        className="text-xs"
                        style={{
                          color: g.members.length ? '#e2e8f0' : '#475569',
                        }}
                      >
                        {g.members.length || '0'}
                      </span>
                    </td>
                    <td className="py-3 px-3.5" onClick={() => openEdit(g)}>
                      <span
                        className="text-xs"
                        style={{
                          color: g.clientAccess.length ? '#e2e8f0' : '#475569',
                        }}
                      >
                        {g.clientAccess.length || '0'}
                      </span>
                    </td>
                    <td className="py-3 px-3.5">
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => openEdit(g)}
                          className="border border-rule-3 rounded-md py-1 px-2.5 text-blue-400 cursor-pointer text-[11px]"
                          style={{
                            background: 'none',
                          }}
                        >
                          Modifier
                        </button>
                        <button
                          onClick={() => handleDelete(g.id)}
                          className="rounded-md py-1 px-2.5 text-red-300 cursor-pointer text-[11px]"
                          style={{
                            background: 'none',
                            border: '1px solid #7f1d1d',
                          }}
                        >
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {
        // ─── Edit Modal with Tabs ─────────────────────────────
        editing && (
          <div
            className="fixed flex items-center justify-center z-[1000]"
            style={{
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.6)',
            }}
            onClick={() => setEditingId(null)}
          >
            <div
              className="bg-card border border-rule rounded-2xl flex flex-col overflow-hidden"
              style={{
                width: 620,
                maxWidth: '92vw',
                maxHeight: '85vh',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {' '}
              <div
                className="flex justify-between items-start"
                style={{
                  padding: '20px 24px 0',
                }}
              >
                <div>
                  <div className="text-lg font-bold text-fg">{editing.name}</div>
                  {editing.description && (
                    <div
                      className="text-xs text-muted-2"
                      style={{
                        marginTop: 3,
                      }}
                    >
                      {editing.description}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setEditingId(null)}
                  className="border-none text-muted-2 cursor-pointer text-2xl"
                  style={{
                    background: 'none',
                    lineHeight: 1,
                    padding: '0 4px',
                  }}
                >
                  ×
                </button>
              </div>{' '}
              <div
                className="flex border-b border-rule"
                style={{
                  gap: 0,
                  padding: '16px 24px 0',
                }}
              >
                {['general', 'members', 'clients'].map((tab) => {
                  const labels = {
                    general: 'Général',
                    members: 'Membres (' + editing.members.length + ')',
                    clients: 'Clients (' + editing.clientAccess.length + ')',
                  };
                  const active = editTab === tab;
                  return (
                    <button
                      key={tab}
                      onClick={() => setEditTab(tab)}
                      className={`bg-transparent border-none py-2.5 px-5 text-[13px] cursor-pointer transition-colors ${
                        active ? 'text-fg font-semibold' : 'text-muted-2'
                      }`}
                      style={{
                        borderBottom: active ? '2px solid #3b82f6' : '2px solid transparent',
                      }}
                    >
                      {labels[tab]}
                    </button>
                  );
                })}
              </div>{' '}
              <div className="p-6 overflow-y-auto flex-1">
                {
                  // ── General Tab ──
                  editTab === 'general' && (
                    <div>
                      <div className="flex flex-col gap-3.5">
                        <div>
                          <label className="text-[11px] text-muted-2 mb-1 block">Nom du groupe</label>
                          <input
                            value={editForm.name || ''}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                name: e.target.value,
                              })
                            }
                            className={inputS}
                          />
                        </div>
                        <div>
                          <label className="text-[11px] text-muted-2 mb-1 block">Description</label>
                          <input
                            value={editForm.description || ''}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                description: e.target.value,
                              })
                            }
                            className={inputS}
                            placeholder="Description optionnelle..."
                          />
                        </div>
                        <div
                          className="py-3 px-3.5 rounded-xl text-xs text-muted"
                          style={{
                            background: 'rgba(59,130,246,0.05)',
                            border: '1px solid rgba(59,130,246,0.15)',
                          }}
                        >
                          {'ℹ Les groupes définissent les '}
                          <strong className="text-fg-2">clients visibles</strong>
                          {' pour leurs membres. Les '}
                          <strong className="text-fg-2">permissions d'actions</strong>
                          {' (connexion distante, commandes, etc.) sont définies par le '}
                          <strong className="text-fg-2">rôle</strong>
                          {" de l'utilisateur."}
                        </div>
                      </div>
                      <div className="flex gap-2 mt-5 justify-end">
                        <button
                          onClick={() => handleDelete(editing.id)}
                          className="rounded-lg py-2 px-4 text-red-300 cursor-pointer text-xs"
                          style={{
                            background: 'none',
                            border: '1px solid #7f1d1d',
                            marginRight: 'auto',
                          }}
                        >
                          Supprimer le groupe
                        </button>
                        <button
                          onClick={saveEdit}
                          className="bg-blue-500 border-none rounded-lg py-2 px-5 text-white cursor-pointer text-xs font-semibold"
                        >
                          Enregistrer
                        </button>
                      </div>
                    </div>
                  )
                }
                {
                  // ── Members Tab ──
                  editTab === 'members' && (
                    <div>
                      <div className="mb-4">
                        <input
                          placeholder="Rechercher un utilisateur à ajouter..."
                          value={memberSearch}
                          onChange={(e) => setMemberSearch(e.target.value)}
                          className={inputS}
                        />
                      </div>
                      {memberSearch && availableUsers.length > 0 && (
                        <div
                          className="mb-4 flex flex-col gap-1 overflow-y-auto bg-card border border-rule-2 rounded-xl p-2"
                          style={{
                            maxHeight: 180,
                          }}
                        >
                          {availableUsers.slice(0, 10).map((u) => (
                            <div
                              key={u.id}
                              onClick={() => {
                                addMember(u.id);
                                setMemberSearch('');
                              }}
                              className="flex items-center gap-2.5 py-2 px-3 rounded-lg cursor-pointer transition-colors hover:bg-rule"
                            >
                              <div
                                className="w-[30px] bg-rule flex items-center justify-center text-muted-2 text-xs font-semibold flex-shrink-0"
                                style={{
                                  height: 30,
                                  borderRadius: '50%',
                                }}
                              >
                                {(u.displayName || u.email).charAt(0).toUpperCase()}
                              </div>
                              <div
                                className="flex-1"
                                style={{
                                  minWidth: 0,
                                }}
                              >
                                <div className="text-[13px] text-fg-2 font-medium">
                                  {u.displayName || u.email}
                                </div>
                                {u.displayName && <div className="text-[11px] text-muted-3">{u.email}</div>}
                              </div>
                              <span
                                className="text-[11px] font-semibold flex-shrink-0"
                                style={{
                                  color: '#3b82f6',
                                }}
                              >
                                + Ajouter
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {memberSearch && availableUsers.length === 0 && (
                        <div className="text-muted-3 text-xs mb-4 py-2.5 px-3.5 bg-card rounded-lg">
                          Aucun utilisateur trouvé
                        </div>
                      )}
                      <div
                        className="text-[11px] font-semibold text-muted-2 uppercase mb-2.5"
                        style={{
                          letterSpacing: '0.05em',
                        }}
                      >
                        {'Membres actuels (' + editing.members.length + ')'}
                      </div>
                      {editing.members.length === 0 ? (
                        <div
                          className="text-muted-3 text-[13px] bg-card rounded-xl text-center"
                          style={{
                            padding: '16px',
                          }}
                        >
                          Aucun membre — recherchez un utilisateur ci-dessus pour en ajouter.
                        </div>
                      ) : (
                        <div className="flex flex-col gap-1.5">
                          {editing.members.map((m) => {
                            const user = allUsers.find((u) => u.id === m.userId);
                            return (
                              <div
                                key={m.userId}
                                className="flex items-center gap-2.5 py-2.5 px-3.5 bg-card border border-rule-2 rounded-xl"
                              >
                                <div
                                  className="bg-rule flex items-center justify-center text-muted text-[13px] font-semibold flex-shrink-0"
                                  style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: '50%',
                                  }}
                                >
                                  {(m.displayName || m.email || '?').charAt(0).toUpperCase()}
                                </div>
                                <div
                                  className="flex-1"
                                  style={{
                                    minWidth: 0,
                                  }}
                                >
                                  <div className="text-[13px] font-medium text-fg-2">
                                    {m.displayName || m.email}
                                  </div>
                                  <div className="text-[11px] text-muted-3">
                                    {user ? user.email : m.userId}
                                  </div>
                                </div>
                                <button
                                  onClick={() => removeMember(m.userId)}
                                  title="Retirer du groupe"
                                  className="rounded-md py-1 px-2.5 text-red-300 cursor-pointer text-[11px] flex-shrink-0"
                                  style={{
                                    background: 'none',
                                    border: '1px solid #7f1d1d',
                                  }}
                                >
                                  Retirer
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )
                }
                {
                  // ── Clients Tab ──
                  editTab === 'clients' && (
                    <div>
                      <div className="flex gap-2 mb-4 flex-wrap items-end">
                        <div className="flex-1 min-w-[200px]">
                          <label className="text-[11px] text-muted-2 mb-1 block">
                            Rechercher un client à ajouter
                          </label>
                          <input
                            placeholder="Nom du client..."
                            value={clientSearch}
                            onChange={(e) => setClientSearch(e.target.value)}
                            className={inputS}
                          />
                        </div>
                        <div className="w-[170px] flex-none">
                          <label className="text-[11px] text-muted-2 mb-1 block">Niveau d'accès</label>
                          <select
                            value={addClientLevel}
                            onChange={(e) => setAddClientLevel(parseInt(e.target.value))}
                            className={`${inputS} cursor-pointer appearance-none bg-no-repeat bg-[right_0.6rem_center] pr-8`}
                            style={{
                              backgroundImage: chevronBg,
                            }}
                          >
                            <option value={1}>Lecture</option>
                            <option value={2}>Opérateur</option>
                            <option value={3}>Contrôle total</option>
                          </select>
                        </div>
                      </div>
                      {clientSearch && availableClients.length > 0 && (
                        <div
                          className="mb-4 flex flex-col gap-1 overflow-y-auto bg-card border border-rule-2 rounded-xl p-2"
                          style={{
                            maxHeight: 180,
                          }}
                        >
                          {availableClients.slice(0, 10).map((c) => (
                            <div
                              key={c.id}
                              onClick={() => {
                                addClient(c.id);
                                setClientSearch('');
                              }}
                              className="flex items-center justify-between py-2 px-3 rounded-lg cursor-pointer transition-colors hover:bg-rule"
                            >
                              <div>
                                <div className="text-[13px] text-fg-2 font-medium">{c.name}</div>
                                {c.sitesCount != null && (
                                  <div className="text-[11px] text-muted-3">{c.sitesCount + ' site(s)'}</div>
                                )}
                              </div>
                              <span
                                className="text-[11px] font-semibold"
                                style={{
                                  color: '#3b82f6',
                                }}
                              >
                                {'+ Ajouter (' + levelLabels[addClientLevel] + ')'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {clientSearch && availableClients.length === 0 && (
                        <div className="text-muted-3 text-xs mb-4 py-2.5 px-3.5 bg-card rounded-lg">
                          Aucun client trouvé
                        </div>
                      )}
                      <div
                        className="text-[11px] font-semibold text-muted-2 uppercase mb-2.5"
                        style={{
                          letterSpacing: '0.05em',
                        }}
                      >
                        {'Clients autorisés (' + editing.clientAccess.length + ')'}
                      </div>
                      {editing.clientAccess.length === 0 ? (
                        <div
                          className="text-muted-3 text-[13px] bg-card rounded-xl text-center"
                          style={{
                            padding: '16px',
                          }}
                        >
                          Aucun client — les membres de ce groupe ne verront aucune donnée.
                        </div>
                      ) : (
                        <div className="flex flex-col gap-1.5">
                          {editing.clientAccess.map((ca) => (
                            <div
                              key={ca.clientId}
                              className="flex items-center gap-2.5 py-2.5 px-3.5 bg-card border border-rule-2 rounded-xl"
                            >
                              <div
                                className="flex-1"
                                style={{
                                  minWidth: 0,
                                }}
                              >
                                <div className="text-[13px] font-semibold text-fg">
                                  {ca.clientName || ca.clientId}
                                </div>
                              </div>
                              <select
                                value={ca.accessLevel}
                                onChange={(e) =>
                                  changeClientLevel(ca.clientId, parseInt(e.target.value))
                                }
                                title="Modifier le niveau d'accès"
                                aria-label={
                                  "Niveau d'accès pour " + (ca.clientName || ca.clientId)
                                }
                                className={`rounded-md text-[11px] font-semibold flex-shrink-0 cursor-pointer appearance-none border-0 outline-none bg-no-repeat bg-[right_0.45rem_center] py-[3px] pl-2.5 pr-[26px] ${
                                  levelClasses[ca.accessLevel] || levelClasses[1]
                                }`}
                                style={{
                                  backgroundImage: chevronBg,
                                }}
                              >
                                {/* Les options héritent sinon la couleur de la pastille,
                                    illisible sur le fond natif du menu déroulant. */}
                                <option value={1} className="bg-card text-fg-2">
                                  Lecture
                                </option>
                                <option value={2} className="bg-card text-fg-2">
                                  Opérateur
                                </option>
                                <option value={3} className="bg-card text-fg-2">
                                  Contrôle total
                                </option>
                              </select>
                              <button
                                onClick={() => removeClient(ca.clientId)}
                                title="Retirer l'accès"
                                className="rounded-md py-1 px-2.5 text-red-300 cursor-pointer text-[11px] flex-shrink-0"
                                style={{
                                  background: 'none',
                                  border: '1px solid #7f1d1d',
                                }}
                              >
                                Retirer
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                }
              </div>
            </div>
          </div>
        )
      }
    </div>
  );
}
